/**
 * YanbuOfferBanner — iter143
 * ───────────────────────────
 * Renders (at most) ONE banner inside the business subscription panel:
 *   1) Eligible + not-yet-active  → "Activate offer" CTA
 *   2) Currently active + ≤15 days left → countdown warning banner
 *   3) Currently active + > 15 days left → silent success (no banner)
 *   4) Not eligible OR already redeemed → hidden entirely
 *
 * Strict eligibility is server-enforced; the FE simply mirrors what
 * the eligibility endpoint returns.  Never trust client state.
 */
import React, { useEffect, useState } from "react";
import { apiClient } from "../../../lib/api";
import { useI18n } from "../../../lib/i18n";
import { toast } from "sonner";
import { Sparkles, Clock3, Loader2, CheckCircle2 } from "lucide-react";

export default function YanbuOfferBanner({ businessId, subscription, onActivated }) {
  const { lang } = useI18n();
  const [elig, setElig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [justActivated, setJustActivated] = useState(false);

  useEffect(() => {
    let dead = false;
    apiClient
      .get(`/platform/plans/business/${businessId}/yanbu-offer/eligibility`)
      .then(({ data }) => { if (!dead) setElig(data); })
      .catch(() => { if (!dead) setElig({ eligible: false, reasons: ["error"] }); })
      .finally(() => { if (!dead) setLoading(false); });
    return () => { dead = true; };
  }, [businessId]);

  const activate = async () => {
    setActivating(true);
    try {
      await apiClient.post(`/platform/plans/business/${businessId}/yanbu-offer/activate`);
      setJustActivated(true);
      toast.success(
        lang === "ar"
          ? "مبارك! تم تفعيل عرض معارض ينبع المؤسسين — 3 أشهر مجانًا 🎉"
          : "Congrats! Yanbu Founding Showrooms 3-month free offer activated 🎉",
        { duration: 6000 }
      );
      onActivated && onActivated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || (lang === "ar" ? "تعذّر التفعيل" : "Activation failed"));
    } finally {
      setActivating(false);
    }
  };

  if (loading) return null;
  if (!elig) return null;

  // Countdown banner — show when currently on Yanbu plan with ≤ 15 days left
  const isYanbuActive = elig.is_current_yanbu;
  const daysLeft = subscription?.promo_days_left;
  const showCountdown = isYanbuActive && typeof daysLeft === "number" && daysLeft <= 15;

  if (showCountdown) {
    return (
      <div
        className="rounded-2xl border p-5 flex items-center gap-4"
        style={{ background: "linear-gradient(135deg,#fff8e6,#ffe9c2)", borderColor: "#d4a24a" }}
        data-testid="yanbu-offer-countdown"
      >
        <div className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "#a8893e", color: "#fff" }}>
          <Clock3 className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <div className="text-[11px] uppercase tracking-widest font-bold text-[#8a6f2e]">
            {lang === "ar" ? "عرض معارض ينبع المؤسسين — 3 أشهر مجانًا" : "Yanbu Founding Showrooms — 3 Months Free"}
          </div>
          <div className="font-bold text-[#3a2a10]">
            {lang === "ar"
              ? `تبقّى ${daysLeft} يوماً على انتهاء اشتراكك المجاني`
              : `${daysLeft} days remaining on your free subscription`}
          </div>
          <div className="text-xs text-[#8a6f2e] mt-1">
            {lang === "ar"
              ? "اختَر باقة مدفوعة قبل انتهاء المدة لضمان استمرار خدمات المعرض دون انقطاع."
              : "Pick a paid plan before expiry to keep your services running uninterrupted."}
          </div>
        </div>
      </div>
    );
  }

  // "Just activated" (before parent re-fetches subscription) — welcome banner
  if (justActivated) {
    return (
      <div
        className="rounded-2xl border p-5 flex items-center gap-4"
        style={{ background: "linear-gradient(135deg,#f0fdf4,#dcfce7)", borderColor: "#16a34a" }}
        data-testid="yanbu-offer-welcome"
      >
        <div className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "#16a34a", color: "#fff" }}>
          <CheckCircle2 className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <div className="text-[11px] uppercase tracking-widest font-bold text-[#15803d]">
            {lang === "ar" ? "مبارك عليكم!" : "Congratulations!"}
          </div>
          <div className="font-bold text-[#14532d]">
            {lang === "ar"
              ? "تم تفعيل «عرض معارض ينبع المؤسسين — 3 أشهر مجانًا» 🎉"
              : "Yanbu Founding Showrooms — 3 Months Free activated 🎉"}
          </div>
          <div className="text-xs text-[#166534] mt-1">
            {lang === "ar"
              ? "استمتِع بجميع مزايا الباقة طوال فترة العرض. سنذكّرك قبل انتهاء المدة بـ ١٥ يوماً."
              : "Enjoy all included features. We'll remind you 15 days before it ends."}
          </div>
        </div>
      </div>
    );
  }

  // Eligible + not yet active → show activation CTA
  if (elig.eligible) {
    return (
      <div
        className="rounded-2xl border p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4"
        style={{ background: "linear-gradient(135deg,#0a0a0a,#1f1614)", borderColor: "#a8893e" }}
        data-testid="yanbu-offer-cta"
      >
        <div className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "#a8893e", color: "#fff" }}>
          <Sparkles className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <div className="text-[11px] uppercase tracking-widest font-bold" style={{ color: "#d4ae6a" }}>
            {lang === "ar" ? "عرض معارض ينبع المؤسسين — 3 أشهر مجانًا" : "Yanbu Founding Showrooms — 3 Months Free"}
          </div>
          <div className="font-display text-xl text-white mt-1">
            {lang === "ar"
              ? "٣ أشهر مجاناً — بدون أي رسوم"
              : "3 Months Free — No fees"}
          </div>
          <div className="text-xs mt-1" style={{ color: "#d4d4d4" }}>
            {lang === "ar"
              ? "تم التحقق من مقر معرضك في ينبع ومن توثيق النشاط. فعّل عرضك الآن."
              : "Your Yanbu-based showroom is verified. Activate your offer now."}
          </div>
        </div>
        <button
          type="button"
          onClick={activate}
          disabled={activating}
          className="px-6 py-3 rounded-full font-bold text-sm inline-flex items-center gap-2 disabled:opacity-60"
          style={{ background: "#a8893e", color: "#fff" }}
          data-testid="yanbu-offer-activate-btn"
        >
          {activating && <Loader2 className="w-4 h-4 animate-spin" />}
          {lang === "ar" ? "فعّل العرض الآن" : "Activate now"}
        </button>
      </div>
    );
  }

  // Ineligible / already redeemed / not verified → hidden entirely.
  return null;
}
